import { useEffect, useMemo, useRef, useState } from "react";
import { fetchContentRecommend, fetchLinkPreview, isAbortError, type ContentRecommendHit } from "../lib/api";
import { categoryLabelAr } from "../lib/categories";
import { articleFromHit } from "../lib/sections";
import type { SectionArticle } from "../lib/segment-types";
import { ArticleUrlBox } from "./ArticleUrlBox";
import { Bidi } from "./primitives";
import { IconClose } from "./icons";

/**
 * Browse Altibbi content for one category and add it to a section.
 *
 * The editor never types a URL: picking an article carries its real Altibbi
 * link with it, which is both less work and the only way the links in a sent
 * newsletter can be trusted.
 */
export function ArticlePicker({
  category, used, onAdd, onEnrich, onClose, allowFeatured,
}: {
  category: string | null;
  used: string[];
  onAdd: (article: SectionArticle, asFeatured: boolean) => void;
  /** Fill in what the article's own page carries, once it has been read. */
  onEnrich?: (id: string, patch: Partial<SectionArticle>) => void;
  onClose: () => void;
  allowFeatured?: boolean;
}) {
  const [hits, setHits] = useState<ContentRecommendHit[]>([]);
  // The search is debounced by 300ms, so the picker opened, waited, and spent
  // that wait telling the writer "Nothing published to show yet" — a verdict on
  // a catalogue it had not asked anything of. It starts out loading instead.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [term, setTerm] = useState("");
  // A search is ANDed with the category, so a piece that exists under another
  // category returns nothing here. Widening is one click rather than a dead end.
  const [allCategories, setAllCategories] = useState(false);

  /**
   * Add a piece, then fill in its picture from its own page.
   *
   * `content_index` has no picture column, so a search cannot carry one. The
   * article's page does, and reading it costs one request for the piece being
   * added rather than one for every result. If it fails the link is still
   * added — a missing picture is a field to fill, not a reason to refuse.
   */
  const choose = async (hit: ContentRecommendHit, asFeatured: boolean) => {
    const article = articleFromHit(hit);
    onAdd(article, asFeatured);
    if (!hit.url) return;
    try {
      const meta = await fetchLinkPreview(hit.url);
      if (meta.image || meta.excerpt) {
        onEnrich?.(article.id, {
          image: meta.image,
          excerpt: article.excerpt ?? meta.excerpt,
        });
      }
    } catch {
      /* the link is added either way */
    }
  };
  const searchRef = useRef<HTMLInputElement>(null);
  const usedKey = used.map((u) => u.replace(/\/+$/, "")).join("\n");
  const usedSet = useMemo(() => new Set(usedKey ? usedKey.split("\n") : []), [usedKey]);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await fetchContentRecommend({
          category: allCategories ? undefined : (category ?? undefined),
          q: term.trim() || undefined,
          kinds: ["article", "term", "news"],
          signal: ac.signal,
        });
        if (!ac.signal.aborted) { setHits(rows.filter((h) => h.url)); setError(null); }
      } catch (err) {
        // "The catalogue is down" and "the catalogue has nothing" are
        // different answers, and an editor needs to know which one they got.
        if (!isAbortError(err) && !ac.signal.aborted) {
          setHits([]);
          setError(err instanceof Error ? err.message : "The article catalogue is not reachable.");
        }
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => { ac.abort(); window.clearTimeout(t); };
  }, [category, term, allCategories]);

  return (
    <div className="nl-picker ui-enter" role="dialog" aria-label="Choose an article">
      <div className="nl-picker-head">
        <div>
          <div className="nl-picker-title">
            {category ? <>Recommended for <Bidi>{categoryLabelAr(category)}</Bidi></> : "Recommended articles"}
          </div>
          <div className="nl-picker-sub">
            {category
              ? "Published Altibbi content in this category. Links come with the article."
              : "Pick a category on this section to scope these to it."}
          </div>
        </div>
        <button type="button" className="nl-picker-close" onClick={onClose} aria-label="Close"><IconClose /></button>
      </div>

      <input
        ref={searchRef}
        className="nl-input"
        placeholder="Search within this category…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        aria-label="Search articles"
      />

      {allCategories && (
        <p className="nl-picker-scope">
          Searching every category.{" "}
          <button type="button" onClick={() => setAllCategories(false)}>
            Back to <Bidi>{categoryLabelAr(category ?? "")}</Bidi>
          </button>
        </p>
      )}

      {/* For an article the catalogue search has not indexed yet. */}
      <details className="nl-picker-byurl">
        <summary>Or add one by its address</summary>
        <ArticleUrlBox onAdd={(article) => onAdd(article, allowFeatured ?? false)} />
      </details>

      <div className="nl-picker-list">
        {loading && hits.length === 0 && <p className="nl-picker-empty">Looking up Altibbi content…</p>}
        {!loading && error && (
          <p className="nl-picker-empty is-error">
            {error} Nothing is shown rather than standing in content that does not exist.
          </p>
        )}
        {/* "Nothing here" and "nothing here that matches what you typed" are
            different answers, and the second one has a way out. */}
        {!loading && !error && hits.length === 0 && (
          <div className="nl-picker-empty">
            {term.trim() && category && !allCategories ? (
              <>
                <p>
                  Nothing in <strong><Bidi>{categoryLabelAr(category)}</Bidi></strong> matches
                  {" "}“{term.trim()}”.
                </p>
                <button type="button" className="nl-picker-add" onClick={() => setAllCategories(true)}>
                  Search every category instead
                </button>
              </>
            ) : term.trim() ? (
              <p>Nothing published matches “{term.trim()}”.</p>
            ) : category && !allCategories ? (
              <p>
                Nothing published in <strong><Bidi>{categoryLabelAr(category)}</Bidi></strong> yet.
              </p>
            ) : (
              <p>Nothing published to show yet.</p>
            )}
          </div>
        )}
        {hits.map((hit) => {
          const already = hit.url ? usedSet.has(hit.url.replace(/\/+$/, "")) : false;
          return (
            <article key={`${hit.page_type}-${hit.url}`} className="nl-picker-card">
              <div className="nl-picker-copy">
                <span className="nl-picker-kind">
                  {[hit.category, hit.kind].filter(Boolean).join(" · ")}
                </span>
                <span className="nl-picker-name"><Bidi>{hit.title}</Bidi></span>
                {hit.excerpt && (
                  <span className="nl-picker-excerpt"><Bidi>{hit.excerpt}</Bidi></span>
                )}
              </div>
              <div className="nl-picker-actions">
                {allowFeatured && (
                  <button
                    type="button"
                    className="nl-picker-add is-primary"
                    disabled={already}
                    onClick={() => void choose(hit, true)}
                  >
                    {already ? "Added" : "+ Add as featured"}
                  </button>
                )}
                {!allowFeatured && (
                  <button
                    type="button"
                    className="nl-picker-add is-primary"
                    disabled={already}
                    onClick={() => void choose(hit, false)}
                  >
                    {already ? "Added" : "+ Add article"}
                  </button>
                )}
                {hit.url && (
                  <a className="nl-picker-open" href={hit.url} target="_blank" rel="noopener noreferrer">
                    Open
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
